import {Box, createStyles, MultiSelect, Select, Stack, Text} from '@mantine/core';
import React, {useMemo, useRef} from 'react';

import SearchInput from 'search/inputs/SearchInput';

import {AllowedEventTrigger, EventTriggerConfig} from './RoutineTypes';

const SELECT_DROPDOWN_OFFSET_PX = 12;

interface EventTriggerConfigFormProps {
  config: Partial<EventTriggerConfig>;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  allowedTriggers: Array<AllowedEventTrigger>;
}

const useStyles = createStyles((theme) => ({
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap',
  },
  fieldLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    flexShrink: 0,
  },
  searchInputContainer: {
    flex: 1,
    minWidth: 160,
    maxWidth: 300,
    // Align SearchInput disabled surface with Mantine disabled inputs in this form.
    '& .chipSelectInput.is-disabled': {
      backgroundColor: theme.colors.gray[1],
      borderColor: theme.colors.gray[4],
    },
    // ChipSelectInput renders an inner disabled input with its own gray background.
    // Make it transparent so the outer disabled surface matches Mantine MultiSelect.
    '& .chipSelectInput.is-disabled .chipSelectInput-input': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  },
  selectDropdown: {
    // Select does not expose its internal Popover offset. Cancel Mantine's
    // default 8px offset plus the Nucleus default 8px arrow adjustment.
    '&[data-position^="bottom"]': {
      marginTop: -SELECT_DROPDOWN_OFFSET_PX,
    },
    '&[data-position^="top"]': {
      marginTop: SELECT_DROPDOWN_OFFSET_PX,
    },
  },
}));

/**
 * Build a display label for an (objectType, field) trigger.
 * e.g., "entry" + "Review Status" → "Entry review status"
 */
// TODO(nweeden): Have the backend provide a pre-formatted display label instead of building one client-side
function getTriggerLabel(objectType: string, fieldDisplayName: string): string {
  const typeLabel = objectType.replace(/([A-Z])/g, ' $1').trim();
  return `${typeLabel.charAt(0).toUpperCase()}${typeLabel.slice(1)} ${fieldDisplayName.toLowerCase()}`;
}

export default function EventTriggerConfigForm({
  config,
  onChange,
  allowedTriggers,
}: EventTriggerConfigFormProps): JSX.Element {
  const {classes} = useStyles();
  const toValuesInputRef = useRef<HTMLInputElement>(null);

  // Flatten triggers into selectable options: one per (objectType, field) pair.
  // Keep metadata separate from the Select data items to avoid spreading custom
  // props onto DOM elements.
  const triggerOptions = useMemo(() => {
    const selectData: Array<{value: string; label: string}> = [];
    const metadata = new Map<string, {objectType: string; field: string; allowedValues: Array<string>}>();
    for (const trigger of allowedTriggers) {
      for (const f of trigger.fields) {
        const key = `${trigger.objectType}::${f.field}`;
        selectData.push({value: key, label: getTriggerLabel(trigger.objectType, f.displayName)});
        metadata.set(key, {
          objectType: trigger.objectType,
          field: f.field,
          allowedValues: f.allowedValues,
        });
      }
    }
    return {selectData, metadata};
  }, [allowedTriggers]);

  const selectedTriggerKey =
    config.object_type && config.field ? `${config.object_type}::${config.field}` : null;

  const selectedTrigger =
    selectedTriggerKey != null ? triggerOptions.metadata.get(selectedTriggerKey) : undefined;

  // Ensure the Select data always includes the current value so the label displays
  // even if allowedTriggers is still loading.
  const triggerSelectData = useMemo(() => {
    if (selectedTriggerKey == null || triggerOptions.selectData.some((d) => d.value === selectedTriggerKey)) {
      return triggerOptions.selectData;
    }
    return [{value: selectedTriggerKey, label: selectedTriggerKey}, ...triggerOptions.selectData];
  }, [triggerOptions.selectData, selectedTriggerKey]);

  const valueOptions = useMemo(
    () => (selectedTrigger?.allowedValues ?? []).map((v) => ({value: v, label: v})),
    [selectedTrigger]
  );

  // Ensure the MultiSelect data includes current to_values so they display during loading.
  const toValuesSelectData = useMemo(() => {
    const currentValues = config.to_values ?? [];
    const existingValues = new Set(valueOptions.map((v) => v.value));
    const missing = currentValues.filter((v) => !existingValues.has(v));
    if (missing.length === 0) {
      return valueOptions;
    }
    return [...valueOptions, ...missing.map((v) => ({value: v, label: v}))];
  }, [valueOptions, config.to_values]);

  return (
    <Stack spacing="md">
      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>Run routine after</Text>
        {/* TODO(nweeden): Align input heights and colors more precisely — SearchInput renders slightly differently than Mantine */}
        <Select
          placeholder="Select event"
          value={selectedTriggerKey}
          onChange={(val) => {
            const option = val != null ? triggerOptions.metadata.get(val) : undefined;
            if (option) {
              onChange({
                object_type: option.objectType,
                field: option.field,
                to_values: [],
                object_ids: config.object_ids ?? [],
              });
            }
          }}
          data={triggerSelectData}
          classNames={{dropdown: classes.selectDropdown}}
          size="lg"
          sx={{flex: 1, minWidth: 160}}
        />
      </div>

      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>changes to</Text>
        <MultiSelect
          placeholder="Select values"
          value={config.to_values ?? []}
          onChange={(vals) => {
            onChange({to_values: vals});
            toValuesInputRef.current?.blur();
          }}
          data={toValuesSelectData}
          classNames={{dropdown: classes.selectDropdown}}
          size="lg"
          sx={{flex: 1, minWidth: 160}}
          disabled={selectedTrigger == null}
          ref={toValuesInputRef}
        />
        <Text className={classes.fieldLabel}>for</Text>
        <Box className={classes.searchInputContainer}>
          {config.object_type != null ? (
            <SearchInput
              types={[config.object_type]}
              selected={config.object_ids ?? []}
              onSelect={(id) => {
                if (id != null) {
                  onChange({object_ids: [...(config.object_ids ?? []), id]});
                }
              }}
              onRemove={(id) => {
                if (id != null) {
                  onChange({object_ids: (config.object_ids ?? []).filter((x) => x !== id)});
                }
              }}
              hideSelectedResults={true}
              isMulti={true}
              placeholder={`Search for ${config.object_type}`}
              disabled={selectedTrigger == null}
              dropLeft={true}
            />
          ) : (
            <SearchInput
              types={['_disabled']}
              selected={[]}
              onSelect={() => {}}
              onRemove={() => {}}
              placeholder="Select an event first"
              disabled={true}
              dropLeft={true}
            />
          )}
        </Box>
      </div>
    </Stack>
  );
}
